import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import {
  House,
  ThumbsUp,
  ListBullets,
  PaintBrush,
  Gear,
  CaretRight,
  SignOut,
  Question,
  List,
  File,
  User,
  FileText,
  ChartBar,
  CreditCard,
} from '@phosphor-icons/react';

const monoFont = "'IBM Plex Mono', monospace";

interface ClientPage {
  id: string;
  name: string;
  slug: string;
}

interface CmsSidebarProps {
  activeTab: string;
  activeProjectId: string | null;
  onSelectTab: (tab: string, projectId?: string | null) => void;
  onOpenTutorial: () => void;
  onOpenGettingStarted?: () => void;
  onSignOut: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export function CmsSidebar({
  activeTab,
  activeProjectId,
  onSelectTab,
  onOpenTutorial,
  onOpenGettingStarted,
  onSignOut,
  isOpen,
  onClose,
}: CmsSidebarProps) {
  const [clientPages, setClientPages] = useState<ClientPage[]>([]);
  const [approvalExpanded, setApprovalExpanded] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  useEffect(() => {
    const loadClientPages = async () => {
      const { data } = await supabase.rpc('get_client_pages_safe');
      if (data) setClientPages((data as any[]).map(d => ({ id: d.id, name: d.name, slug: d.slug })));
    };

    loadClientPages();

    const handleProjectsChange = () => {
      loadClientPages();
    };
    window.addEventListener('cms-projects-updated', handleProjectsChange);
    return () => window.removeEventListener('cms-projects-updated', handleProjectsChange);
  }, []);

  useEffect(() => {
    if (activeTab === 'aprovacao') {
      setApprovalExpanded(true);
    }
  }, [activeTab]);

  const handleSelectSection = (slug: string) => {
    onSelectTab(slug, null);
    if (window.innerWidth < 768) onClose();
  };

  const navItemStyle = (isActive: boolean): React.CSSProperties => ({
    fontFamily: monoFont,
    fontSize: 13,
    fontWeight: 300,
    padding: '10px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: isActive ? '#F7F6F2' : 'rgba(247,246,242,0.5)',
    backgroundColor: isActive ? 'rgba(247,246,242,0.06)' : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
  });

  const projectItemStyle = (isActive: boolean): React.CSSProperties => ({
    fontFamily: monoFont,
    fontSize: 13,
    fontWeight: 300,
    padding: '8px 24px 8px 48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: isActive ? '#F7F6F2' : 'rgba(247,246,242,0.5)',
    backgroundColor: isActive ? 'rgba(247,246,242,0.06)' : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
  });

  const separatorStyle: React.CSSProperties = {
    height: 1,
    backgroundColor: 'rgba(247,246,242,0.08)',
    margin: '4px 16px',
  };

  const hoverHandlers = (isActive: boolean) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = 'rgba(247,246,242,0.04)';
        e.currentTarget.style.color = 'rgba(247,246,242,0.8)';
      }
    },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = 'rgba(247,246,242,0.5)';
      }
    },
  });

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 z-40"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-screen z-50 flex flex-col transition-transform duration-250 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-[240px] md:translate-x-0'
        }`}
        style={{
          width: 240,
          backgroundColor: 'rgba(247,246,242,0.06)',
          borderRight: '0.5px solid rgba(247,246,242,0.08)',
        }}
      >
        {/* Zone 1: Identity */}
        <div
          className="flex-none"
          style={{
            padding: '24px 24px 16px 24px',
            borderBottom: '0.5px solid rgba(247,246,242,0.08)',
          }}
        >
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 13,
              fontWeight: 300,
              color: '#666666',
              textTransform: 'uppercase',
            }}
          >
            CRIAAA
          </span>
        </div>

        {/* Zone 2: Navigation */}
        <div className="flex-1 overflow-y-auto" style={{ padding: '16px 0' }}>
          {/* 1. Home */}
          <button
            style={navItemStyle(activeTab === 'home' && !activeProjectId)}
            onClick={() => handleSelectSection('home')}
            {...hoverHandlers(activeTab === 'home' && !activeProjectId)}
          >
            <House size={16} weight="thin" />
            Home
          </button>

          {/* 2. About */}
          <button
            style={navItemStyle(activeTab === 'about')}
            onClick={() => handleSelectSection('about')}
            {...hoverHandlers(activeTab === 'about')}
          >
            <FileText size={16} weight="thin" />
            About
          </button>

          <div style={separatorStyle} />

          {/* 3. Menu */}
          <button
            style={navItemStyle(activeTab === 'menu')}
            onClick={() => handleSelectSection('menu')}
            {...hoverHandlers(activeTab === 'menu')}
          >
            <ListBullets size={16} weight="thin" />
            Menu
          </button>

          <div style={separatorStyle} />

          {/* 4. Aprovação (expandable with client pages) */}
          <button
            style={navItemStyle(activeTab === 'aprovacao')}
            onClick={() => {
              setApprovalExpanded(!approvalExpanded);
              handleSelectSection('aprovacao');
            }}
            {...hoverHandlers(activeTab === 'aprovacao')}
          >
            <ThumbsUp size={16} weight="thin" />
            <span className="flex-1">Aprovação</span>
            <CaretRight
              size={12}
              weight="thin"
              style={{
                transform: approvalExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
                opacity: 0.5,
              }}
            />
          </button>

          {approvalExpanded && clientPages.map(page => {
            const isPageActive = activeTab === 'aprovacao' && activeProjectId === page.id;
            return (
              <button
                key={page.id}
                style={projectItemStyle(isPageActive)}
                onClick={() => {
                  onSelectTab('aprovacao', page.id);
                  if (window.innerWidth < 768) onClose();
                }}
                {...hoverHandlers(isPageActive)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <File size={14} weight="thin" style={{ opacity: 0.4, flexShrink: 0 }} />
                  {page.name}
                </span>
              </button>
            );
          })}

          <div style={separatorStyle} />

          {/* 5. Aparência */}
          <button
            style={navItemStyle(activeTab === 'aparencia')}
            onClick={() => handleSelectSection('aparencia')}
            {...hoverHandlers(activeTab === 'aparencia')}
          >
            <PaintBrush size={16} weight="thin" />
            Aparência
          </button>

          {/* Estatísticas */}
          <button
            style={navItemStyle(activeTab === 'estatisticas')}
            onClick={() => handleSelectSection('estatisticas')}
            {...hoverHandlers(activeTab === 'estatisticas')}
          >
            <ChartBar size={16} weight="thin" />
            Estatísticas
          </button>

          <div style={separatorStyle} />

          {/* 6. Configurações */}
          <button
            style={navItemStyle(activeTab === 'configuracoes')}
            onClick={() => handleSelectSection('configuracoes')}
            {...hoverHandlers(activeTab === 'configuracoes')}
          >
            <Gear size={16} weight="thin" />
            Configurações
          </button>

          {/* 7. Planos */}
          <button
            style={navItemStyle(activeTab === 'planos')}
            onClick={() => handleSelectSection('planos')}
            {...hoverHandlers(activeTab === 'planos')}
          >
            <CreditCard size={16} weight="thin" />
            Planos
          </button>

          {/* 8. Perfil */}
          <button
            style={navItemStyle(activeTab === 'perfil')}
            onClick={() => handleSelectSection('perfil')}
            {...hoverHandlers(activeTab === 'perfil')}
          >
            <User size={16} weight="thin" />
            Perfil
          </button>

          <div style={separatorStyle} />

          {/* 8. Tutorial */}
          <button
            style={navItemStyle(false)}
            onClick={onOpenTutorial}
            {...hoverHandlers(false)}
          >
            <Question size={16} weight="thin" />
            Tutorial
          </button>
        </div>

        {/* Zone 3: Footer */}
        <div
          className="flex-none"
          style={{
            borderTop: '0.5px solid rgba(247,246,242,0.08)',
            padding: '16px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {onOpenGettingStarted && (
            <button
              onClick={() => {
                onOpenGettingStarted();
                if (window.innerWidth < 768) onClose();
              }}
              style={{
                fontFamily: monoFont,
                fontSize: 12,
                fontWeight: 300,
                color: 'rgba(247,246,242,0.2)',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'color 0.15s ease',
                padding: 0,
                textAlign: 'left',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'rgba(247,246,242,0.5)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(247,246,242,0.2)')}
            >
              ○ Ver guia de início
            </button>
          )}
          <button
            onClick={() => setShowSignOutConfirm(true)}
            className="flex items-center gap-2"
            style={{
              fontFamily: monoFont,
              fontSize: 13,
              fontWeight: 300,
              color: 'rgba(247,246,242,0.3)',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              transition: 'color 0.15s ease',
              padding: 0,
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(247,246,242,0.7)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(247,246,242,0.3)')}
          >
            <SignOut size={14} weight="thin" />
            Sair
          </button>

          <ConfirmDialog
            open={showSignOutConfirm}
            onOpenChange={setShowSignOutConfirm}
            title="Sair do CMS"
            description="Tem certeza que deseja sair? Você precisará fazer login novamente para acessar o painel."
            confirmLabel="Sair"
            cancelLabel="Cancelar"
            onConfirm={onSignOut}
            destructive
          />
        </div>
      </aside>
    </>
  );
}

// Mobile hamburger button component
export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="md:hidden fixed top-4 left-4 z-30 p-2"
      style={{
        backgroundColor: 'rgba(247,246,242,0.06)',
        border: '0.5px solid rgba(247,246,242,0.08)',
      }}
    >
      <List size={24} weight="thin" style={{ color: 'rgba(247,246,242,0.6)' }} />
    </button>
  );
}
